/**
 * HR Org-structure bulk-import API client.
 *
 * POST /api/v1/hr/org-structure/import
 *   dryRun: true  → derive + validate the plan, write nothing
 *   dryRun: false → commit (create functions → departments → teams, audited)
 *
 * One CSV, three entities. Headers: `function,department,team` — one row per
 * team carrying its full path. Column order is free (read by header name).
 */
import Papa from 'papaparse'
import { z } from 'zod'
import { apiRequest } from './client'

export const OrgStructureRowSchema = z.object({
  company: z.string().min(1, 'company is required'),
  function: z.string().min(1, 'function is required'),
  department: z.string().min(1, 'department is required'),
  team: z.string().min(1, 'team is required'),
  // Optional. When present, set the head of the function / department node;
  // resolved server-side to an employee by name or email. Blank → untouched.
  functionHead: z.string().optional(),
  departmentHead: z.string().optional(),
})
export type OrgStructureRow = z.infer<typeof OrgStructureRowSchema>

const LevelPlanSchema = z.object({
  create: z.array(z.string()),
  existing: z.array(z.string()),
  reparent: z.array(z.string()),
})
export type LevelPlan = z.infer<typeof LevelPlanSchema>

export const OrgStructureImportResultSchema = z.object({
  dryRun: z.boolean(),
  totalRows: z.number().int(),
  companies: LevelPlanSchema,
  functions: LevelPlanSchema,
  departments: LevelPlanSchema,
  teams: LevelPlanSchema,
  // "<unit path> → <head name>" for each head assigned / to-be-assigned.
  heads: z.array(z.string()).default([]),
  errors: z.array(z.object({ rowIndex: z.number().int(), message: z.string() })),
})
export type OrgStructureImportResult = z.infer<typeof OrgStructureImportResultSchema>

// Accept the canonical headers plus a couple of friendly aliases.
const HEADER_ALIASES: Readonly<Record<string, keyof OrgStructureRow>> = {
  company: 'company',
  function: 'function',
  department: 'department',
  'new department': 'department',
  team: 'team',
  'new team': 'team',
  'function head': 'functionHead',
  function_head: 'functionHead',
  'department head': 'departmentHead',
  department_head: 'departmentHead',
  // papaparse runs transformHeader twice; the 2nd pass lowercases our already
  // camelCased output, so map the lowercased form back to itself (idempotent).
  functionhead: 'functionHead',
  departmenthead: 'departmentHead',
}

export interface ParsedCsv {
  rows: OrgStructureRow[]
  errors: Array<{ line: number; message: string }>
}

/** Parse a raw CSV string into validated org-structure rows. */
export function parseOrgStructureCsv(text: string): ParsedCsv {
  const parsed = Papa.parse<Record<string, string>>(text, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (h) => HEADER_ALIASES[h.trim().toLowerCase()] ?? h.trim().toLowerCase(),
  })
  const rows: OrgStructureRow[] = []
  const errors: Array<{ line: number; message: string }> = []
  parsed.data.forEach((raw, i) => {
    const result = OrgStructureRowSchema.safeParse({
      company: (raw.company ?? '').trim(),
      function: (raw.function ?? '').trim(),
      department: (raw.department ?? '').trim(),
      team: (raw.team ?? '').trim(),
      // Omit when blank so the row carries no head → server leaves it untouched.
      functionHead: (raw.functionHead ?? '').trim() || undefined,
      departmentHead: (raw.departmentHead ?? '').trim() || undefined,
    })
    if (result.success) {
      rows.push(result.data)
    } else {
      errors.push({
        line: i + 2, // +1 for header, +1 for 1-based
        message: result.error.issues.map((issue) => issue.message).join(', '),
      })
    }
  })
  return { rows, errors }
}

export async function importOrgStructure(
  rows: OrgStructureRow[],
  dryRun: boolean,
): Promise<OrgStructureImportResult> {
  const raw = await apiRequest<unknown>('/api/v1/hr/org-structure/import', {
    method: 'POST',
    body: { rows, dryRun },
  })
  return OrgStructureImportResultSchema.parse(raw)
}
